import { useMemo } from 'react';
import { useStoredState } from 'hooks/useStoredState';
import { FIT_DEFAULTS, StoredFit, clampFitWindow, reviveFit } from './auxFitStorage';
import {
  LinearFit,
  ScatterSplit,
  fitLinePoints,
  linearFit,
  linearFitFixedIntercept,
  partitionByRange,
} from './auxSensorChannels';

/** One series' split plus the fit over its included points. */
export interface ScatterFitResult extends ScatterSplit {
  fit: LinearFit | null;
  /** Two-point line spanning the fitted points, or null when there is no fit. */
  fitLine: [number, number][] | null;
}

export interface UseScatterFit {
  showFit: boolean;
  setShowFit: (value: boolean) => void;
  /** Fit window in x-axis units, clamped to the current data range. */
  window: { low: number; high: number };
  setWindow: (window: { low: number; high: number }) => void;
  manualOffsetOn: boolean;
  setManualOffsetOn: (value: boolean) => void;
  manualOffset: number;
  setManualOffset: (value: number) => void;
  /** Per-series split + fit, index-aligned with the `series` argument. */
  results: ScatterFitResult[];
}

/**
 * The fit half of a scatter plot: the "show fit" toggle, the x-axis fit window,
 * the optional held-intercept offset, and the resulting per-series split and
 * least-squares fit.
 *
 * State is persisted per `storageKey`, so it survives both changing runs and the
 * unmount `CollapsibleSection` performs when its section is collapsed. The window
 * is stored raw and **clamped against `maxX` on read**: a window carried over from
 * a run with a wider range collapses to the new run's maximum rather than
 * stranding the plot, without overwriting the stored preference.
 */
export function useScatterFit(
  series: [number, number][][],
  maxX: number,
  storageKey: string,
  allowManualOffset = false,
): UseScatterFit {
  const [stored, setStored] = useStoredState<StoredFit>(
    `runAuxSensor.fit.${storageKey}`,
    FIT_DEFAULTS,
    reviveFit,
  );

  const patch = (next: Partial<StoredFit>) => setStored({ ...stored, ...next });

  const { showFit, manualOffsetOn, manualOffset } = stored;
  const { low, high } = clampFitWindow(stored.window, maxX);
  const holdOffset = allowManualOffset && manualOffsetOn;

  const results = useMemo<ScatterFitResult[]>(
    () => series.map((points) => {
      const split = partitionByRange(points, showFit ? low : 0, showFit ? high : Infinity);
      // Only fit when the readout is actually shown — the fit is two passes over
      // every point, and off-state results are discarded.
      const fit = showFit
        ? (holdOffset ? linearFitFixedIntercept(split.included, manualOffset) : linearFit(split.included))
        : null;
      return { ...split, fit, fitLine: fitLinePoints(split.included, fit) };
    }),
    [series, showFit, low, high, holdOffset, manualOffset],
  );

  return {
    showFit,
    setShowFit: (value) => patch({ showFit: value }),
    window: { low, high },
    setWindow: (value) => patch({ window: value }),
    manualOffsetOn,
    setManualOffsetOn: (value) => patch({ manualOffsetOn: value }),
    manualOffset,
    setManualOffset: (value) => patch({ manualOffset: value }),
    results,
  };
}
